// Chat (README "Chat"): short messages between everyone on the site, whichever city they watch, over the live count's socket
// (site/js/live/live.js). Pure helpers only, so plain Node can test them (tests/chat.test.mjs): the text rules the page checks
// before it sends and the worker enforces (worker/chat.js imports them from here), the names, and the page's log.
import { shopLinks, shopSale, saleOn } from '../ui/shop.js';

const sale = shopSale();                   // the video loops' sale, or null (the site's posts, below)

export const SAY_MAX = 80;                  // characters in a message, once cleaned (code points: an emoji is one)
export const SAY_CAP = 3;                   // a page's burst of messages,
export const SAY_EVERY_MS = 4000;           // then one more every 4 s
export const CHAT_KEEP = 30;                // the messages the chat keeps, the newest,
export const CHAT_LIFE_MS = 6 * 3600000;    // for 6 hours at most
export const LOG_MAX = 50;                  // what the page's panel holds on to while it's open
export const HIDE_CAP = 5;                  // hides a page may send: a burst of 5,
export const HIDE_EVERY_MS = 10000;         // then one every 10 s

/* ---------- the text rules ---------- */
// What a message is made of once cleaned: every run of spaces, tabs and line breaks is one space, and the characters
// nobody can see are gone (controls, zero-width spaces, the bidi overrides that can turn text around, and the blank
// letters that make an empty-looking message). Zero-width joiners, variation selectors and tag characters stay: emoji
// are made with them. More than three marks on one letter ("zalgo" text that spills over the lines around it) are cut
// to three, which every script needs at most.
const INVISIBLE = /[\p{Cc}\u00AD\u061C\u115F\u1160\u180E\u200B\u200C\u200E\u200F\u202A-\u202E\u2060-\u2064\u2066-\u206F\u2800\u3164\uFEFF\uFFA0\uFFF9-\uFFFB]/gu;
const PILED_MARKS = /(\p{M}{3})\p{M}+/gu;
const LONE_SURROGATE = c => c.length === 1 && c >= '\uD800' && c <= '\uDFFF';

/** A message as it would be posted: cleaned and trimmed (maybe empty, maybe too long), or null for anything but text. */
export function cleanSay(text) {
  if (typeof text !== 'string') return null;
  const whole = Array.from(text, c => (LONE_SURROGATE(c) ? '\uFFFD' : c)).join('');   // String#toWellFormed, older browsers too
  return whole.normalize('NFC').replace(/\s+/gu, ' ').replace(INVISIBLE, '').replace(PILED_MARKS, '$1')
    .replace(/ {2,}/g, ' ').trim();
}

/** Its length as SAY_MAX counts it: code points, so an emoji or a letter from outside Latin counts as one. */
export const sayLength = text => [...text].length;

// Top-level domains a link could end in, written straight after a dot ("name.com") or with the dot spelled out
// ("name dot com", "name(.)com"). The ones that are also everyday words ("in", "me", "live", "lol", …) only count
// straight after a dot with a path after them ("t.me/…", "youtu.be/…"), so "so much fun.lol", "hello.it's late" and
// "the dot in the sky" stay messages.
const TLDS = ['com', 'net', 'org', 'io', 'gg', 'co', 'tv', 'app', 'dev', 'xyz', 'ly', 'info', 'biz', 'ru', 'cn', 'uk', 'de',
  'fr', 'jp', 'br', 'es', 'nl', 'tk', 'ml', 'ga', 'cf', 'sh', 'cc', 'ws', 'su', 'pw', 'eu', 'ca', 'au', 'tr', 'pl', 'kr', 'vn',
  'ph', 'mx', 'ar', 'cl', 'za', 'ng', 'pk', 'ir', 'ua', 'kz', 'gl', 'gd', 'la', 'fm', 'st', 'nu', 'ch', 'se', 'dk', 'fi', 'pt',
  'gr', 'cz', 'hu', 'ro', 'sk', 'ee', 'lt', 'lv', 'bg', 'hr', 'rs', 'si', 'ie', 'nz', 'sg', 'hk', 'tw', 'th', 'ae', 'sa', 'il',
  'eg', 'ma', 'ai', 'cam', 'bet', 'nft', 'sex', 'xxx', 'porn'];
const WORD_TLDS = ['in', 'is', 'it', 'at', 'be', 'no', 'to', 'so', 'me', 'us', 'am', 'do', 'go', 'by', 'my', 'id', 'one', 'pro',
  'fyi', 'art', 'fun', 'live', 'top', 'win', 'club', 'news', 'chat', 'link', 'page', 'cash', 'money', 'games', 'store', 'shop',
  'site', 'online', 'social', 'stream', 'blog', 'click', 'vip', 'bio', 'ink', 'lol'];
const SPELT_DOT = String.raw`\s*(?:\(\s*\.\s*\)|\[\s*\.\s*\]|\(\s*dot\s*\)|\[\s*dot\s*\]|\{\s*dot\s*\})\s*|\s+dot\s+`;
const END = String.raw`(?![\p{L}\p{N}])`;
const LINKS = [
  /\b(?:https?|ftp)\s*:\s*\/\//iu,                                                                  // a scheme
  /\bwww\s*\./iu,
  new RegExp(String.raw`[\p{L}\p{N}]\.(?:${TLDS.join('|')})${END}`, 'iu'),                        // name.com
  new RegExp(String.raw`[\p{L}\p{N}](?:${SPELT_DOT})(?:${TLDS.join('|')})${END}`, 'iu'),              // name dot com
  new RegExp(String.raw`[\p{L}\p{N}]\.(?:${WORD_TLDS.join('|')})\s*\/`, 'iu'),                    // t.me/…
  /[\p{L}\p{N}._%+-]@[\p{L}\p{N}-]+\.\p{L}{2,}/iu,                                                  // an email
  /(?:\+?\d[\s().\/-]*){9,}/u,                                                                       // a phone number
];
// An @ stuck to a name is a handle, except for the adjectives of the names here: "@sleepy fox, hello" is a reply. So
// is an @ before a chosen name (below), which `named` says: it's handed what follows the @.
const HANDLE = /(?:^|[^\p{L}\p{N}_])@([\p{L}\p{N}_.]{2,})/gu;

/** Whether a message carries a link, an email address, an @handle or a phone number, none of which may be posted. */
export function linkIn(text, named = () => false) {
  if (LINKS.some(re => re.test(text))) return true;
  for (const match of text.matchAll(HANDLE)) {
    if (ADJECTIVES.includes(match[1].toLowerCase())) continue;
    if (!named(text.slice(match.index + match[0].indexOf('@') + 1))) return true;
  }
  return false;
}

/* ---------- names: two words from a browser's author key ---------- */
// The worker hands every browser an author key (worker/chat.js: 8 hex digits of a salted hash of its random id, never
// the id), the same in all its tabs and on every visit. The page makes a name of it, and a colour for the name.
export const ADJECTIVES = ['sleepy', 'rainy', 'misty', 'dreamy', 'quiet', 'cozy', 'gentle', 'velvet', 'amber', 'lunar',
  'starry', 'drowsy', 'mellow', 'snowy', 'foggy', 'dusky', 'hazy', 'silver', 'golden', 'calm', 'wandering', 'little',
  'curious', 'humble', 'lazy', 'soft', 'warm', 'moonlit', 'cloudy', 'twilight', 'breezy', 'mossy'];
export const ANIMALS = ['fox', 'owl', 'cat', 'otter', 'moth', 'hare', 'panda', 'koala', 'deer', 'heron', 'crow', 'seal',
  'bear', 'frog', 'snail', 'sparrow', 'raccoon', 'badger', 'lynx', 'whale', 'turtle', 'hedgehog', 'bat', 'swan', 'robin',
  'penguin', 'mole', 'duck', 'wolf', 'lemur', 'finch', 'bunny'];

/** Whether `a` is an author key: 8 lowercase hex digits. */
export const isAuthor = a => typeof a === 'string' && /^[0-9a-f]{8}$/.test(a);

/** The name for an author key, e.g. 'sleepy fox' ('someone' for anything else). */
export function nameOf(a) {
  if (!isAuthor(a)) return 'someone';
  const n = parseInt(a, 16);
  return `${ADJECTIVES[n & 31]} ${ANIMALS[(n >>> 5) & 31]}`;
}

/**
 * The names the panel shows for the author keys in `authors`, told apart, as a Map from key to name. A name nobody else
 * there has stays as it is; people who share one (there are 1,024, so in one room it happens) each get the start of
 * their key after it, "sleepy fox ·3f", as many digits as it takes to tell them all apart, 2 at least. The name comes
 * from a key's last 10 bits, so keys with the same name differ in their first 6 digits: that's the most it takes. Keys
 * that aren't author keys are left out. A key with a chosen name in `nicks` (a Map from key to name) has that one,
 * which nobody else has (the worker sees to it), and leaves its random name to whoever else has it.
 */
export function namesOf(authors, nicks = new Map()) {
  const byName = new Map(), names = new Map();
  for (const a of new Set(authors)) {
    if (!isAuthor(a)) continue;
    if (nicks.has(a)) names.set(a, nicks.get(a));
    else byName.set(nameOf(a), [...(byName.get(nameOf(a)) ?? []), a]);
  }
  for (const [name, keys] of byName) {
    if (keys.length === 1) { names.set(keys[0], name); continue; }
    let n = 2;
    while (new Set(keys.map(a => a.slice(0, n))).size < keys.length) n++;
    for (const a of keys) names.set(a, `${name} ·${a.slice(0, n)}`);
  }
  return names;
}

/** The hue (0-359) its name is written in. */
export const hueOf = a => (isAuthor(a) ? (parseInt(a, 16) >>> 10) % 360 : 0);

/* ---------- chosen names: a signed-in account's own name instead of its random one (docs/APP_REFERENCE.md "Accounts") ---------- */
// The worker keeps them by author key (worker/chat.js ChatLog.setNick) and sends each with its messages (`n`); a page
// can't claim one. The rules here are the page's and the worker's both; the worker adds its word filter, that nobody
// else has the name (by its fold, below) and the day between changes.
export const NICK_MIN = 3, NICK_MAX = 16;   // characters (code points), once cleaned like a message (cleanSay)
export const NICK_EVERY_MS = 86400000;      // a new name once a day; going back to the random one, any time

// Letters (any script, with their marks), digits and single spaces, and at least one letter.
const NICK_CHARS = /^[\p{L}\p{M}\p{N}]+(?: [\p{L}\p{M}\p{N}]+)*$/u;
// Read the same when told apart: lowercase, accents gone from Latin letters (not the marks other scripts spell with),
// digits and Cyrillic letters that look like Latin ones read as those (as worker/words.js reads text), no spaces.
export const LOOKALIKE = {
  0: 'o', 1: 'i', 3: 'e', 4: 'a', 5: 's', 7: 't', 8: 'b', 9: 'g', '@': 'a', $: 's', '!': 'i', '|': 'i', '€': 'e', '¡': 'i',
  ı: 'i', ß: 'ss', æ: 'ae', œ: 'oe', ø: 'o', đ: 'd', ł: 'l', þ: 'th',
  а: 'a', в: 'b', е: 'e', ё: 'e', к: 'k', м: 'm', н: 'h', о: 'o', р: 'p', с: 'c', т: 't', у: 'y', х: 'x', і: 'i', ј: 'j',
  ѕ: 's', ԁ: 'd', ԛ: 'q', ԝ: 'w', α: 'a', ο: 'o', ρ: 'p',
};
/** A name as names are told apart: 'Mîka 1' and 'mika i' are one ('mikai'). */
export const foldNick = name => Array.from(name.toLowerCase().normalize('NFKD').replace(/(?<=[a-z])\p{M}+/gu, ''), c => LOOKALIKE[c] ?? c)
  .join('').replace(/[^\p{L}\p{M}\p{N}]/gu, '');

// Nobody can be the site, its maker or its staff, or anyone's random name (or start with one: "sleepy fox 3f" would
// pass for the tag two people with that name get). Also read with every held-down letter once ("saaafa").
const RESERVED = ['safa', 'safaelmali', 'lofi', 'loficity', 'loficities', 'admin', 'administrator', 'mod', 'moderator',
  'official', 'staff', 'support', 'team', 'system', 'bot', 'owner', 'everyone', 'someone', 'anonymous', 'you'];
const RESERVED_IN = ['loficities', 'safaelmali', 'admin', 'moderator', 'official'];
const RANDOM_FOLDS = ADJECTIVES.flatMap(adjective => ANIMALS.map(animal => adjective + animal));
const once = w => w.replace(/(.)\1+/g, '$1');

/** Whether a name is one nobody may choose. */
export function reservedNick(name) {
  const forms = [foldNick(name), once(foldNick(name))];
  return forms.some(f => RESERVED.some(r => f === r || f === once(r)) || RESERVED_IN.some(r => f.includes(r) || f.includes(once(r)))
    || RANDOM_FOLDS.some(r => f.startsWith(r) || f.startsWith(once(r))));
}

/**
 * Why a cleaned name (cleanSay) can't be chosen: 'short', 'long', 'chars' (anything but letters, digits and single
 * spaces, or no letter), 'link' (a phone number, "shop dot com") or 'reserved'; null when it can, as far as the page
 * can tell.
 */
export function nickRefusal(name) {
  if (typeof name !== 'string') return 'chars';
  const n = sayLength(name);
  if (n < NICK_MIN) return 'short';
  if (n > NICK_MAX) return 'long';
  if (!NICK_CHARS.test(name) || !/\p{L}/u.test(name)) return 'chars';
  if (linkIn(name)) return 'link';
  if (reservedNick(name)) return 'reserved';
  return null;
}

/* ---------- mentions: "@sleepy fox", "@Mika" ---------- */
// A mention is "@" and a whole name, any case: a random one (an adjective and an animal from the lists above), or a
// chosen one the page knows (`nicks`, told apart by their fold), with nothing but single spaces inside and no letter or
// digit straight after; the longest that fits. It's only text: the worker posts it like any other (linkIn lets an @
// through before an adjective or a chosen name), and every page finds the names in it for itself.
const NAME_RUN = /^[\p{L}\p{M}\p{N}]+(?: [\p{L}\p{M}\p{N}]+)*/u;
const LONGEST = Math.max(NICK_MAX, Math.max(...ADJECTIVES.map(w => w.length)) + 1 + Math.max(...ANIMALS.map(w => w.length)));

/**
 * The names `rest` (what follows an "@") could be, shortest first: its first word, its first two, and so on, as long as
 * a name can be ("Mika Rose, hi" → 'Mika', 'Mika Rose'). None that runs into "_", or into "." and more letters, as a
 * handle or an address does ("mika_shop", "mika.shop").
 */
export function namePrefixes(rest) {
  const run = NAME_RUN.exec(rest)?.[0];
  if (!run) return [];
  const out = [];
  let end = 0;
  for (const word of run.split(' ')) {
    end += (end ? 1 : 0) + word.length;
    const name = rest.slice(0, end);
    if (sayLength(name) > LONGEST) break;
    if (!/^(?:_|\.[\p{L}\p{N}])/u.test(rest.slice(end))) out.push(name);
  }
  return out;
}

/** Whether `name` is a random name, any case: 'Sleepy Fox'. */
export function isRandomName(name) {
  const [adjective, animal, more] = name.toLowerCase().split(' ');
  return more === undefined && ADJECTIVES.includes(adjective) && ANIMALS.includes(animal);
}

/** For linkIn: whether what follows an "@" starts with one of the chosen names `nicks` (told apart by their fold). */
export function nickAt(nicks) {
  const folds = new Set([...nicks].map(foldNick));
  return rest => namePrefixes(rest).some(name => folds.has(foldNick(name)));
}

/** The mentions in `text`: { index, length, name }, `name` the random one lowercase or the chosen one in `nicks`, lowercase. */
function mentionsOf(text, nicks = []) {
  const chosen = new Map([...nicks].map(n => [foldNick(n), n.toLowerCase()]));
  const found = [];
  for (const { index } of text.matchAll(/(?<![\p{L}\p{N}_])@/gu)) {
    for (const name of namePrefixes(text.slice(index + 1)).reverse()) {
      const is = isRandomName(name) ? name.toLowerCase() : chosen.get(foldNick(name));
      if (is) { found.push({ index, length: name.length + 1, name: is }); break; }
    }
  }
  return found;
}

/** The names a message mentions, lowercase, each once, in the order they come: ['sleepy fox', 'mika']. */
export const mentionsIn = (text, nicks = []) => [...new Set(mentionsOf(text, nicks).map(m => m.name))];

/**
 * A message cut into what the panel writes: [{ text }] for plain text and [{ text, name }] for a mention, its text as
 * written ("@Sleepy Fox") and its name lowercase ('sleepy fox'). Joined back, the texts are the message.
 */
export function partsOf(text, nicks = []) {
  const parts = [];
  let from = 0;
  for (const { index, length, name } of mentionsOf(text, nicks)) {
    if (index > from) parts.push({ text: text.slice(from, index) });
    parts.push({ text: text.slice(index, index + length), name });
    from = index + length;
  }
  if (from < text.length) parts.push({ text: text.slice(from) });
  return parts;
}

/**
 * Whether message `m` mentions author key `me` by the name they go by (`nick`, their chosen one, or their random one),
 * and isn't their own. `nicks` are the other chosen names the page knows, so "@Mika Rose" isn't taken for "@Mika".
 */
export const mentionsMe = (m, me, { nick = null, nicks = [] } = {}) => isAuthor(me) && m.a !== me
  && mentionsIn(m.text, nick ? [...nicks, nick] : nicks).includes((nick ?? nameOf(me)).toLowerCase());

// The mention ding (Settings: Mention sound): a soft glass "ding-ding" in the track's key, on its next beat (player.tap,
// site/js/audio/taps.js), for a message that mentions you while you're away from the chat: its panel shut, or the page
// in the background. At most one every DING_GAP_MS, so a run of mentions rings once, and only while music plays.
export const MENTION_DING = { voice: 'glass', from: 76, notes: [7, 12], gap: 0.13, decay: 0.3, gain: 2 };
export const DING_GAP_MS = 20000;

/**
 * Whether new message `m` rings the mention ding for author key `me` (who goes by `nick`; `nicks` as mentionsMe): it
 * mentions them, its author isn't in `hidden`, they're `away` from the chat, and the last ding (`last`, ms) was
 * DING_GAP_MS or more before `now`.
 */
export const dingsFor = (m, me, { nick = null, nicks = [], hidden = new Set(), away = true, last = -Infinity, now = 0 } = {}) =>
  away && now - last >= DING_GAP_MS && !hidden.has(m.a) && mentionsMe(m, me, { nick, nicks });

/** How a reply to author key `a` (who goes by `nick`, if they chose one) starts in the text box: '@sleepy fox ' (empty for anything but an author key). */
export const replyTo = (a, nick = null) => (isAuthor(a) ? `@${nick ?? nameOf(a)} ` : '');

/* ---------- who's in the chat: the panel's header ---------- */
/**
 * How many other people have the chat open right now, in any city (the header is only seen with your own panel open),
 * from the worker's `chat_stats`: its `viewers`, panels open and in view, a browser with several tabs counted once. The
 * page takes itself out when the count has it (`counted`: it had told the worker its panel was open when the count
 * came). null without a count to go by (no stats yet, or from a worker without them).
 */
export function inChat(stats, counted = false) {
  const total = stats?.viewers?.total;
  if (!Number.isSafeInteger(total) || total < 0) return null;
  return counted ? Math.max(0, total - 1) : total;
}

/** Its words in the header: '3 in chat', or 'no one in chat'. */
export const inChatLabel = n => (n ? `${n.toLocaleString('en-US')} in chat` : 'no one in chat');

/** How long ago, short: 'now' within the minute, then '5m', then '1h' to '5h' for the older ones. */
export function ago(ms) {
  const m = Math.floor(Math.max(0, ms) / 60000);
  return m < 1 ? 'now' : m < 60 ? `${m}m` : `${Math.floor(m / 60)}h`;
}

/* ---------- the site's own posts: "Follow on X", "Buy me a coffee", "Watch live", and "Get video loops" during a sale ---------- */
// Now and then the worker posts for the site itself (worker/chat.js ChatLog.post, worker/index.js sitePost): a message
// whose author is SITE, which is no author key, so a page from before the posts drops it (messageOf). The worker only
// says which one (`kind`); what it says and where its link goes are the page's own, here, so nothing the socket sends
// can ever become a link. One at a time: a new one takes the last one's place (withMessage). While the video loops are
// on sale (site/js/ui/shop.js SHOP.sale) there is a fourth, which says so; it goes when the sale does, from a page
// opened after its `until` and from one still open then, which stops showing the one it has (shown) and drops one made
// after (postOf).
export const SITE = 'site';
export const SITE_NAME = 'Lofi Cities';
export const POST_EVERY_MS = 3 * 3600000;   // one every 3 hours at most, and only into a chat that's alive (ChatLog.postDue)
export const POSTS = {
  x: { text: 'New cities land here first. Follow @loficitiesapp on X', label: 'Follow on X', url: 'https://x.com/loficitiesapp', destination: 'x-app' },
  coffee: { text: 'Lofi Cities is free. Ask for a city with a coffee and get thanked by name', label: 'Buy me a coffee', url: 'https://buymeacoffee.com/safaelmali', destination: 'buymeacoffee' },
  youtube: { text: 'Lofi Cities is live on YouTube too. Put a city on the big screen', label: 'Watch live', url: 'https://www.youtube.com/live/avKYkQr4PJ0', destination: 'youtube' },
  ...(sale && { loops: { text: sale.chat, label: 'Get video loops', url: shopLinks().store, destination: 'gumroad' } }),
};
export const POST_KINDS = Object.keys(POSTS);

/** Whether a message is one of the site's own posts. */
export const isPost = m => m?.a === SITE;

/** The post after one of `kind`, in turn (the first after none, or after one this table no longer has). */
export const nextPost = kind => POST_KINDS[(POST_KINDS.indexOf(kind) + 1) % POST_KINDS.length];

/** A post from the worker as the page keeps it, { id, a: SITE, kind, text, at }, or null for junk or a kind it doesn't know. */
function postOf(m) {
  if (!Number.isSafeInteger(m.id) || !Number.isFinite(m.at) || typeof m.kind !== 'string' || !Object.hasOwn(POSTS, m.kind)) return null;
  if (m.kind === 'loops' && !saleOn(sale, m.at)) return null;   // the sale ended while the page was open
  return { id: m.id, a: SITE, kind: m.kind, text: POSTS[m.kind].text, at: m.at };
}

/* ---------- reactions: an emoji under a message ---------- */
// Anyone in the chat can put one of six emoji under a message (their own too) and take it back. The worker keeps who
// put which for as long as the message stays (worker/chat.js ChatLog.react) and always sends a message's reactions
// whole, [[emoji, [author keys]]]: with the message in the history, and to every chat page at each change (`re`), so
// a page never counts for itself. The site's own posts take none.
export const RE_EMOJI = ['❤️', '😂', '👍', '🔥', '✨', '😢'];
export const RE_PEOPLE_MAX = 30;            // people under one emoji of one message: past that, it's full
export const RE_CAP = 8;                    // reactions a page may send, put or taken back: a burst of 8,
export const RE_EVERY_MS = 1500;            // then one every 1.5 s
export const RE_NAMES_MAX = 4;              // names a chip lists before "and 3 more"

/**
 * A message's reactions from the worker as the page keeps them: [[emoji, [author keys]]] in RE_EMOJI's order, each
 * emoji and each key under it once, RE_PEOPLE_MAX keys at most, an emoji nobody is under left out. [] for none or junk.
 */
export function reactionsOf(r) {
  if (!Array.isArray(r)) return [];
  const by = new Map();
  for (const pair of r) {
    if (!Array.isArray(pair) || !RE_EMOJI.includes(pair[0]) || !Array.isArray(pair[1])) continue;
    by.set(pair[0], [...new Set([...(by.get(pair[0]) ?? []), ...pair[1].filter(isAuthor)])].slice(0, RE_PEOPLE_MAX));
  }
  return RE_EMOJI.filter(e => by.get(e)?.length).map(e => [e, by.get(e)]);
}

/**
 * The chosen names of the people under a message's reactions, from the worker's `rn` ({ author key: name }, beside
 * `r` in the history and in `re`), as [[key, name]]: the panel says who by the name they go by, also when they haven't
 * written anything it shows. [] for none or junk.
 */
export const reactorNicks = m => (m?.rn !== null && typeof m?.rn === 'object' && !Array.isArray(m.rn)
  ? Object.entries(m.rn).filter(([a, n]) => isAuthor(a) && nickOf(n) !== null) : []);

// A message with reactions `r` (from reactionsOf) instead of the ones it had: no `r` at all for none.
const reacted = ({ r: had, ...m }, r) => (r.length ? { ...m, r } : m);

/** The log with message `id`'s reactions replaced by `r` (from reactionsOf). The site's posts take none. */
export const withReactions = (log, id, r) => log.map(m => (m.id === id && !isPost(m) ? reacted(m, r) : m));

/** The log without author `a`'s reactions: a hush takes them along with their messages. */
export const withoutReactor = (log, a) => log.map(m => (m.r?.some(([, keys]) => keys.includes(a))
  ? reacted(m, m.r.map(([e, keys]) => [e, keys.filter(k => k !== a)]).filter(([, keys]) => keys.length)) : m));

/**
 * The chips under message `m`: [{ e, who, mine }], `who` the author keys under emoji `e` in the order they came,
 * without the people in `hidden` (hidden on this device: their reactions too), `mine` whether `me` is one of them.
 */
export const chipsOf = (m, { me = null, hidden = new Set() } = {}) => (m.r ?? [])
  .map(([e, keys]) => ({ e, who: keys.filter(a => !hidden.has(a)) })).filter(c => c.who.length)
  .map(c => ({ ...c, mine: c.who.includes(me) }));

/** Who is under a chip, in words: 'sleepy fox', 'you and sleepy fox', 'you, sleepy fox and rainy owl', then 'a, b, c, d and 3 more'. */
export function whoLabel(names, max = RE_NAMES_MAX) {
  const said = names.slice(0, max), more = names.length - said.length;
  if (more) return `${said.join(', ')} and ${more} more`;
  return said.length > 1 ? `${said.slice(0, -1).join(', ')} and ${said.at(-1)}` : said[0] ?? '';
}

/* ---------- the page's log ---------- */
// A city id as the worker sends it (worker/presence.js CITIES): the page only shows one it knows.
const CITY_ID = /^[a-z]+(?:-[a-z]+)*$/;

/**
 * A message from the worker as the page keeps it, { id, a, text, at, city, n, r }, or null for junk. `city` is the one its
 * author was watching when they sent it (the tag after their name), left out when the worker doesn't say (an older one)
 * or says something that isn't a city id; `n` is its author's chosen name, left out when they go by their random one;
 * `r` is its reactions (reactionsOf), left out when it has none. One of the site's own posts comes back as postOf has it.
 */
export function messageOf(m) {
  if (isPost(m)) return postOf(m);
  if (!Number.isSafeInteger(m?.id) || !isAuthor(m.a) || typeof m.text !== 'string' || !Number.isFinite(m.at)) return null;
  const text = cleanSay(m.text);
  if (!text || sayLength(text) > SAY_MAX) return null;
  const city = typeof m.city === 'string' && m.city.length <= 32 && CITY_ID.test(m.city) ? m.city : null;
  const n = nickOf(m.n), r = reactionsOf(m.r);
  return { id: m.id, a: m.a, text, at: m.at, ...(city && { city }), ...(n && { n }), ...(r.length && { r }) };
}

/** A chosen name as the worker sends it (`n`, `nick`), or null for none or junk. */
export const nickOf = n => (typeof n === 'string' && cleanSay(n) === n && sayLength(n) >= NICK_MIN && sayLength(n) <= NICK_MAX ? n : null);

/**
 * The log with message `m` (from messageOf) in its place by id, once, the newest LOG_MAX at most. One of the site's
 * posts takes the place of the one before it: the newer of the two stays.
 */
export function withMessage(log, m) {
  if (!m || log.some(x => x.id === m.id)) return log;
  const had = isPost(m) ? log.find(isPost) : null;
  if (had && had.id > m.id) return log;
  return [...(had ? log.filter(x => x !== had) : log), m].sort((x, y) => x.id - y.id).slice(-LOG_MAX);
}

/** The chat's history from the worker (its `chat` message's list), cleaned, oldest first. */
export const historyOf = list => (Array.isArray(list) ? list.reduce((log, m) => withMessage(log, messageOf(m)), []) : []);

/**
 * What the panel shows at `now` (the worker's clock): what's still within CHAT_LIFE_MS, without the authors in `hidden`,
 * and without the sale's post once the sale is over, though it came before. The site's post only among people's
 * messages: with none of theirs to show, the panel is empty, not an ad.
 */
export function shown(log, now, hidden = new Set()) {
  const list = log.filter(m => now - m.at < CHAT_LIFE_MS && !hidden.has(m.a) && (m.kind !== 'loops' || saleOn(sale, now)));
  return list.some(m => !isPost(m)) ? list : [];
}

/**
 * What a closed panel still has for this page (the dot on the Chat button): what it would show, said by other people
 * (not `me`, not the site), after the newest message it has shown (`seen`, an id: ids only go up, see ChatLog.add in
 * worker/chat.js).
 */
export const unreadIn = (log, now, { me = null, hidden = new Set(), seen = 0 } = {}) =>
  shown(log, now, hidden).filter(m => m.a !== me && !isPost(m) && m.id > seen);
